import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import api from "../../utils/api";
import toast from "react-hot-toast";

const fmt    = (n) => "GHS " + Number(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2 });

const statusColor = {
  paid:    { bg: "#f0fff4", text: "#276749", dot: "#48bb78" },
  pending: { bg: "#fffbeb", text: "#744210", dot: "#f6ad55" },
  overdue: { bg: "#fff5f5", text: "#742a2a", dot: "#fc8181" },
};

const exportCSV = (invoices, year, quarter) => {
  const headers = ["Invoice #","Date","Vendor","Vendor TIN","Subtotal (GHS)","VAT (GHS)","Total (GHS)","VAT Rate","Quarter","Status","GRA Reported"];
  const rows = invoices.map(inv => [
    inv.invoiceNumber || "",
    inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-GH") : "",
    inv.vendor?.name || "",
    inv.vendor?.tinNumber || "",
    fmtNum(inv.amount?.subtotal),
    fmtNum(inv.amount?.vatAmount),
    fmtNum(inv.amount?.totalAmount),
    `${inv.vatRate || 15}%`,
    inv.fiscalQuarter || "",
    inv.status,
    inv.graReported ? "Yes" : "No",
  ]);
  const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `GRA_VAT_Report_${year}_${quarter}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const GRAReports = () => {
  const [invoices,      setInvoices]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [fiscalYear,    setFiscalYear]    = useState(new Date().getFullYear().toString());
  const [fiscalQuarter, setFiscalQuarter] = useState("all");
  const [markingAll,    setMarkingAll]    = useState(false);

  const fetchGRAInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fiscalYear });
      if (fiscalQuarter !== "all") params.append("fiscalQuarter", fiscalQuarter);
      const res = await api.get(`/invoices/export/gra?${params}`);
      setInvoices(res.data.invoices || []);
    } catch (err) {
      toast.error("Failed to load GRA data");
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, fiscalQuarter]);

  useEffect(() => { fetchGRAInvoices(); }, [fetchGRAInvoices]);

  const totalVat      = invoices.reduce((s, i) => s + (i.amount?.vatAmount   || 0), 0);
  const totalSubtotal = invoices.reduce((s, i) => s + (i.amount?.subtotal    || 0), 0);
  const totalAmount   = invoices.reduce((s, i) => s + (i.amount?.totalAmount || 0), 0);
  const reportedCount = invoices.filter(i => i.graReported).length;
  const pendingCount  = invoices.length - reportedCount;

  const handleMarkAllReported = async () => {
    setMarkingAll(true);
    try {
      await Promise.all(
        invoices.filter(i => !i.graReported).map(i => api.put(`/invoices/${i._id}`, { graReported: true }))
      );
      toast.success("All invoices marked as reported");
      fetchGRAInvoices();
    } catch (err) {
      toast.error("Failed to mark invoices");
    } finally {
      setMarkingAll(false);
    }
  };
  
  const handleMarkOne = async (id) => {
  try {
    await api.put(`/invoices/${id}`, { graReported: true });
       toast.success("Marked as reported");
       fetchGRAInvoices();
     } catch (err) {
       toast.error("Failed to update");
     }
  };

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>GRA VAT Reports</h1>
            <p style={styles.pageSub}>Ghana Revenue Authority — VAT invoice summary for filing</p>
          </div>
          <button onClick={() => exportCSV(invoices, fiscalYear, fiscalQuarter)} style={styles.exportBtn} disabled={invoices.length === 0}>
            ⬇ Export CSV
          </button>
        </div>

        {/* Filters */}
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Fiscal Year</label>
            <select style={styles.select} value={fiscalYear} onChange={e => setFiscalYear(e.target.value)}>
              {["2026","2025","2024","2023"].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Quarter</label>
            <select style={styles.select} value={fiscalQuarter} onChange={e => setFiscalQuarter(e.target.value)}>
              <option value="all">All Quarters</option>
              {["Q1","Q2","Q3","Q4"].map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div style={styles.filterNote}>
            📅 Ghana VAT filing: quarterly within 30 days of quarter end
          </div>
        </div>

        {loading ? (
          <div style={styles.loadingWrap}>Loading GRA data...</div>
        ) : invoices.length === 0 ? (
          <div style={styles.emptyWrap}>
            <div style={styles.emptyIcon}>📋</div>
            <div style={styles.emptyTitle}>No VAT invoices found</div>
            <div style={styles.emptySub}>No invoices marked as VAT invoices for {fiscalYear} {fiscalQuarter !== "all" ? fiscalQuarter : ""}.</div>
            <Link to="/upload" style={styles.emptyLink}>Upload an invoice →</Link>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
              {[
                { label: "Total VAT Invoices", value: invoices.length,    sub: "VAT-eligible invoices",  accent: "#63b3ed", raw: true  },
                { label: "Total Subtotal",      value: fmt(totalSubtotal), sub: "before VAT",             accent: "#f6ad55", raw: false },
                { label: "Total VAT (15%)",     value: fmt(totalVat),      sub: "payable to GRA",         accent: "#fc8181", raw: false },
                { label: "Total Amount",        value: fmt(totalAmount),   sub: "including VAT",          accent: "#68d391", raw: false },
                { label: "Reported to GRA",     value: `${reportedCount} / ${invoices.length}`, sub: `${pendingCount} pending`, accent: "#b794f4", raw: false },
              ].map(card => (
                <div key={card.label} style={styles.summaryCard}>
                  <div style={{ ...styles.cardAccent, background: card.accent }} />
                  <div style={styles.cardLabel}>{card.label}</div>
                  <div style={styles.cardValue}>{card.value}</div>
                  <div style={styles.cardSub}>{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Compliance Bar */}
            <div style={styles.complianceCard}>
              <div style={styles.complianceHeader}>
                <div>
                  <div style={styles.complianceTitle}>Filing Compliance</div>
                  <div style={styles.complianceSub}>{reportedCount} of {invoices.length} invoices marked as reported to GRA</div>
                </div>
                {pendingCount > 0 && (
                  <button onClick={handleMarkAllReported} style={styles.markAllBtn} disabled={markingAll}>
                    {markingAll ? "Marking..." : "✓ Mark All Reported"}
                  </button>
                )}
              </div>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${invoices.length > 0 ? (reportedCount / invoices.length) * 100 : 0}%` }} />
              </div>
              <div style={styles.progressLabels}>
                <span style={styles.progressReported}>✓ {reportedCount} reported</span>
                <span style={styles.progressPending}>⏳ {pendingCount} pending</span>
              </div>
            </div>

            {/* VAT Invoice Table */}
            <div style={styles.tableCard}>
              <div style={styles.tableHeader}>
                <div style={styles.tableTitle}>VAT Invoice Register</div>
                <div style={styles.tableSub}>Period: {fiscalYear} {fiscalQuarter !== "all" ? `· ${fiscalQuarter}` : "· All Quarters"}</div>
              </div>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    {["Invoice #","Date","Vendor","Vendor TIN","Subtotal","VAT (15%)","Total","Status","GRA Filed"].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const sc = statusColor[inv.status] || statusColor.pending;
                    return (
                      <tr key={inv._id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                        <td style={styles.td}><span style={styles.invNum}>{inv.invoiceNumber || "—"}</span></td>
                        <td style={styles.td}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-GH") : "—"}</td>
                        <td style={styles.td}><div style={styles.vendorName}>{inv.vendor?.name || "—"}</div></td>
                        <td style={styles.td}><span style={styles.tin}>{inv.vendor?.tinNumber || "—"}</span></td>
                        <td style={styles.td}>{fmt(inv.amount?.subtotal)}</td>
                        <td style={{ ...styles.td, ...styles.vatCell }}><strong>{fmt(inv.amount?.vatAmount)}</strong></td>
                        <td style={styles.td}>{fmt(inv.amount?.totalAmount)}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, background: sc.bg, color: sc.text }}>
                            <span style={{ ...styles.badgeDot, background: sc.dot }} />
                            {inv.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                         {inv.graReported
                          ? <span style={styles.reportedTag}>✓ Filed</span>
                          : (
                            <button
                             onClick={() => handleMarkOne(inv._id)}
                             style={styles.markOneBtn}
                           >
                            Mark Filed
                           </button>
                          )
                         }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={styles.tfootRow}>
                    <td colSpan={4} style={styles.tfootLabel}>TOTALS ({invoices.length} invoices)</td>
                    <td style={styles.tfootVal}>{fmt(totalSubtotal)}</td>
                    <td style={{ ...styles.tfootVal, color: "#fc8181" }}><strong>{fmt(totalVat)}</strong></td>
                    <td style={styles.tfootVal}><strong>{fmt(totalAmount)}</strong></td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Filing Reminder */}
            <div style={styles.reminderCard}>
              <span style={styles.reminderIcon}>⚠️</span>
              <div>
                <div style={styles.reminderTitle}>GRA Filing Reminder</div>
                <div style={styles.reminderText}>
                  Q1 {fiscalYear} VAT return is due by <strong>30 April {fiscalYear}</strong>. Export this report as CSV and submit via the GRA Taxpayer Portal at <strong>taxpayer.gra.gov.gh</strong>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const styles = {
  shell: { display:"flex", minHeight:"100vh", background:"#f7f8fc", fontFamily:"sans-serif" },
  main: { flex:1, padding:"36px 40px", overflowY:"auto" },
  header: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"24px" },
  pageTitle: { fontSize:"24px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"4px" },
  pageSub: { fontSize:"13px", color:"#718096" },
  exportBtn: { background:"#0a0f1e", color:"#fff", padding:"10px 20px", borderRadius:"8px", fontSize:"13px", fontWeight:"bold", border:"none", cursor:"pointer" },
  filtersRow: { display:"flex", alignItems:"flex-end", gap:"16px", marginBottom:"24px", flexWrap:"wrap" },
  filterGroup: { display:"flex", flexDirection:"column", gap:"5px" },
  filterLabel: { fontSize:"11px", fontWeight:"600", color:"#718096", textTransform:"uppercase", letterSpacing:"0.5px" },
  select: { padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", background:"#fff", color:"#2d3748", outline:"none" },
  filterNote: { fontSize:"12px", color:"#a0aec0", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"8px", padding:"9px 14px", marginLeft:"auto" },
  loadingWrap: { display:"flex", justifyContent:"center", alignItems:"center", height:"300px", color:"#a0aec0", fontSize:"14px" },
  emptyWrap: { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 0", gap:"12px" },
  emptyIcon: { fontSize:"48px" },
  emptyTitle: { fontSize:"18px", fontWeight:"bold", color:"#0a0f1e" },
  emptySub: { fontSize:"14px", color:"#718096" },
  emptyLink: { color:"#f6ad55", fontWeight:"bold", textDecoration:"none", fontSize:"14px" },
  summaryGrid: { display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:"14px", marginBottom:"20px" },
  summaryCard: { background:"#fff", borderRadius:"12px", padding:"18px", border:"1px solid #e2e8f0", position:"relative", overflow:"hidden" },
  cardAccent: { position:"absolute", top:0, left:0, right:0, height:"3px" },
  cardLabel: { fontSize:"10px", color:"#718096", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"8px", marginTop:"6px" },
  cardValue: { fontSize:"18px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"3px" },
  cardSub: { fontSize:"10px", color:"#a0aec0" },
  complianceCard: { background:"#fff", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px" },
  complianceHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"14px" },
  complianceTitle: { fontSize:"14px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"3px" },
  complianceSub: { fontSize:"12px", color:"#718096" },
  markAllBtn: { background:"#f0fff4", color:"#276749", border:"1px solid #c6f6d5", borderRadius:"8px", padding:"8px 16px", fontSize:"12px", fontWeight:"bold", cursor:"pointer" },
  progressBar: { height:"8px", background:"#f0f0f0", borderRadius:"4px", overflow:"hidden", marginBottom:"8px" },
  progressFill: { height:"100%", background:"#48bb78", borderRadius:"4px", transition:"width 0.5s" },
  progressLabels: { display:"flex", justifyContent:"space-between" },
  progressReported: { fontSize:"12px", color:"#48bb78", fontWeight:"600" },
  progressPending: { fontSize:"12px", color:"#f6ad55", fontWeight:"600" },
  tableCard: { background:"#fff", borderRadius:"12px", border:"1px solid #e2e8f0", overflow:"hidden", marginBottom:"20px" },
  tableHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"18px 24px", borderBottom:"1px solid #f0f0f0" },
  tableTitle: { fontSize:"14px", fontWeight:"bold", color:"#0a0f1e" },
  tableSub: { fontSize:"12px", color:"#a0aec0" },
  table: { width:"100%", borderCollapse:"collapse" },
  thead: { background:"#f7f8fc" },
  th: { textAlign:"left", padding:"11px 16px", fontSize:"10px", color:"#a0aec0", textTransform:"uppercase", letterSpacing:"0.5px", fontWeight:"600", borderBottom:"1px solid #e2e8f0" },
  td: { padding:"13px 16px", fontSize:"12px", color:"#2d3748", verticalAlign:"middle" },
  trEven: { background:"#fff" },
  trOdd: { background:"#fafafa" },
  invNum: { fontFamily:"monospace", fontWeight:"bold", color:"#0a0f1e", fontSize:"12px" },
  vendorName: { fontWeight:"600", color:"#0a0f1e" },
  tin: { fontFamily:"monospace", fontSize:"11px", color:"#718096" },
  vatCell: { color:"#e53e3e" },
  badge: { display:"inline-flex", alignItems:"center", gap:"4px", padding:"3px 8px", borderRadius:"20px", fontSize:"11px", fontWeight:"600", textTransform:"capitalize" },
  badgeDot: { width:"5px", height:"5px", borderRadius:"50%" },
  reportedTag: { background:"#f0fff4", color:"#276749", padding:"3px 8px", borderRadius:"6px", fontSize:"11px", fontWeight:"bold" },
  pendingTag: { background:"#fffbeb", color:"#744210", padding:"3px 8px", borderRadius:"6px", fontSize:"11px", fontWeight:"bold" },
  tfootRow: { background:"#f7f8fc", borderTop:"2px solid #e2e8f0" },
  tfootLabel: { padding:"12px 16px", fontSize:"11px", fontWeight:"bold", color:"#0a0f1e", textTransform:"uppercase", letterSpacing:"0.5px" },
  tfootVal: { padding:"12px 16px", fontSize:"13px", fontWeight:"600", color:"#0a0f1e" },
  reminderCard: { background:"#fffbeb", border:"1px solid #f6e05e", borderRadius:"12px", padding:"18px 24px", display:"flex", alignItems:"flex-start", gap:"14px" },
  reminderIcon: { fontSize:"20px", flexShrink:0 },
  reminderTitle: { fontSize:"14px", fontWeight:"bold", color:"#744210", marginBottom:"4px" },
  reminderText: { fontSize:"13px", color:"#975a16", lineHeight:1.6 },
  markOneBtn: { background:"#f0fff4", color:"#276749", border:"1px solid #c6f6d5", borderRadius:"6px", padding:"4px 10px", fontSize:"11px", fontWeight:"bold", cursor:"pointer" },
};

export default GRAReports;